import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { getSubverticalOptions, industrySupportsSubverticals } from '../../../models/UiUtils';
import {
	useBlogCardsCategories,
	useHandwrittenCardCategories,
	useSocialCategories,
	useTemplateIndustryCategoriesQuery,
} from '../../../queries';
import { useAccountPreferencesSubverticalSettingsMutation } from '../../../queries/Account/useAccountPreferencesSubverticalSettingsMutation';
import { Button } from '../../../web/components/Button';
import { Checkbox } from '../../../web/components/Checkbox';
import { ISelectOption, MultiSelect } from '../../../web/components/DeprecatedSelect';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { ContentFormalitySelect } from '../../../web/components/subverticals/ContentFormalitySelect';
import { bs } from '../../../web/styles/styles';
import { s } from './styles';

interface IProps extends IModalContext<Api.ISubverticalSettings> {
	account?: Api.IAccount;
	className?: string;
	styles?: StyleDeclarationValue[];
}

const SubverticalSelectSFC: React.FC<IProps> = props => {
	const { account, className, parentModal } = props;
	const impersonationContext = { account };
	const [selectedSubverticals, setSelectedSubverticals] = React.useState<string[]>(
		account.preferences.subverticalSettings?.subverticals ?? []
	);
	const [selectedOptOuts, setSelectedOptOuts] = React.useState<string[]>(
		account.preferences.subverticalSettings?.contentLibraryCategoryOptOuts ?? []
	);
	const accountIndustry = account.additionalInfo.industry;
	const emailCategoriesQuery = useTemplateIndustryCategoriesQuery({
		industry: accountIndustry,
	});
	const socialCategoriesQuery = useSocialCategories({
		industry: accountIndustry,
	});
	const hwcCategoriesQuery = useHandwrittenCardCategories({
		industry: accountIndustry,
	});
	const blogCategoriesQuery = useBlogCardsCategories({
		industry: accountIndustry,
	});
	const areAllCategoriesLoaded =
		emailCategoriesQuery.isSuccess &&
		socialCategoriesQuery.isSuccess &&
		hwcCategoriesQuery.isSuccess &&
		blogCategoriesQuery.isSuccess;
	const categories = areAllCategoriesLoaded
		? (Array.from(
				new Set([
					...emailCategoriesQuery.data,
					...socialCategoriesQuery.data,
					...hwcCategoriesQuery.data,
					...blogCategoriesQuery.data,
				])
			) as string[])
		: [];

	const subverticalSettingsMutation = useAccountPreferencesSubverticalSettingsMutation({
		onError: error => {
			logger.logApiError('PUT Account/preferences/subverticalSettings', error);
		},
	});
	const isSaving = subverticalSettingsMutation.isPending;
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SubverticalSelect');

	const subverticalOptions: ISelectOption<string>[] = getSubverticalOptions(accountIndustry).map(o => ({
		type: 'checkbox',
		dataContext: o,
		text: o,
		id: o,
	}));
	const optOutOptions: ISelectOption<string>[] = categories.map(c => ({
		type: 'checkbox',
		dataContext: c,
		text: c,
		id: c,
	}));
	const [contentSubscriber, setContentSubscriber] = React.useState(
		account.preferences.subverticalSettings?.contentSubscriber ?? false
	);
	const [formality, setFormality] = React.useState(
		account.preferences.subverticalSettings?.formality ?? Api.ContentFormality.Formal
	);

	const handleSubverticalOptionClick = (option: ISelectOption<string>) => {
		const index = selectedSubverticals.indexOf(option.dataContext);
		if (index > -1) {
			setSelectedSubverticals([...selectedSubverticals].filter(sv => sv !== option.dataContext));
			return;
		}
		setSelectedSubverticals([...selectedSubverticals, option.dataContext]);
	};
	const handleOpOutOptionClick = (option: ISelectOption<string>) => {
		const index = selectedOptOuts.indexOf(option.dataContext);
		if (index > -1) {
			setSelectedOptOuts([...selectedOptOuts].filter(sv => sv !== option.dataContext));
			return;
		}
		setSelectedOptOuts([...selectedOptOuts, option.dataContext]);
	};

	const onChangeNewContent = (e: React.ChangeEvent<HTMLInputElement>) => {
		setContentSubscriber(e.target.checked);
	};

	const handleSave = async () => {
		const subverticalSettings: Api.ISubverticalSettings = {
			contentSubscriber,
			formality,
			subverticals: selectedSubverticals,
			contentLibraryCategoryOptOuts: selectedOptOuts,
		};
		try {
			const result = await subverticalSettingsMutation.mutateAsync({
				subvSettings: subverticalSettings,
				impersonationContext,
			});
			parentModal?.onRequestClose(result?.preferences?.subverticalSettings, true);
		} catch (error) {
			errorMessages.pushApiError(error);
		}
	};

	const onRequestClose = () => {
		parentModal?.onRequestClose(null, true);
	};
	return (
		<div className={`${css(s.container)} subvertical-select ${className || ''}`}>
			<h1 className={css(s.header)}>Industry & Subvertical Settings</h1>
			<section className={css(s.content)}>
				{industrySupportsSubverticals(accountIndustry) && (
					<>
						<h2 className={css(s.title)}>Subvertical</h2>
						<p className={css(s.subverticalSubtext)}>
							Select subvertical content that you would like to appear as a suggestion.
						</p>
						<div className={css(s.multiselectContainer)}>
							<MultiSelect
								options={subverticalOptions}
								onOptionClick={handleSubverticalOptionClick}
								selectedOptions={subverticalOptions.filter(
									o => selectedSubverticals.findIndex(sv => sv === o.dataContext) > -1
								)}
								selectAllIfNoneSelected={false}
								selectedOptionsTitle={selectedOptions => {
									if (selectedOptions.length === 0) {
										return <div>-</div>;
									}
									return (
										<div className={css(s.selectedMultiOption, bs.truncateText)}>
											{selectedOptions.map(o => o.text).join(', ')}
										</div>
									);
								}}
							/>
						</div>
					</>
				)}

				<h2 className={css(s.optOut)}>Opt Out</h2>
				<p className={css(s.optOutSubtext)}>Select any content that you would not like to appear as a suggestion.</p>
				<div className={css(s.multiselectContainer)}>
					{areAllCategoriesLoaded ? (
						<MultiSelect
							options={optOutOptions}
							onOptionClick={handleOpOutOptionClick}
							selectedOptions={optOutOptions.filter(o => selectedOptOuts.findIndex(sv => sv === o.dataContext) > -1)}
							selectAllIfNoneSelected={false}
							selectedOptionsTitle={selectedOptions => {
								if (selectedOptions.length === 0) {
									return <div>-</div>;
								}
								return (
									<div className={css(s.selectedMultiOption, bs.truncateText)}>
										Excluding {selectedOptions.length} {selectedOptions.length === 1 ? 'category' : 'categories'}
									</div>
								);
							}}
						/>
					) : null}
					{!areAllCategoriesLoaded ? (
						<div className={css(s.spinnerContainer)}>
							<LoadingSpinner />
						</div>
					) : null}
				</div>
				<Checkbox
					id='new-content-checkbox'
					type='large'
					onChange={onChangeNewContent}
					checked={contentSubscriber}
					className={css(s.newContentCheckbox)}
				>
					<span className={css(s.text, s.bold)}>Subscribe to new content</span>
				</Checkbox>
				<ContentFormalitySelect formality={formality} setFormality={setFormality} styles={[s.formality]} />
			</section>
			<footer className={css(s.rowContainer)}>
				<Button kind='primary' onClick={handleSave} disabled={isSaving} label='Save' />
				<Button kind='reverse' onClick={onRequestClose} disabled={isSaving} label='Cancel' />
			</footer>
		</div>
	);
};
const SubverticalSelectAsObserver = observer(SubverticalSelectSFC);
const SubverticalSelectWithContext = inject(ModalChildComponentContextKey)(SubverticalSelectAsObserver);
export const SubverticalSelectModal = asModalComponent(SubverticalSelectWithContext, {
	className: 'subvertical-select-modal',
	useDefaultHeader: true,
});
